import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Loader2 } from "lucide-react";
import { useAppData } from "@/contexts/AppDataContext";
import { AddHeroAsinModal } from "@/components/shared/AddHeroAsinModal";
import { landscapeApi } from "@/resources/landscape";
import { productApi } from "@/resources/product";
import { handleApiError } from "@/utils";
import { parsePrioritySearchPhrases } from "@/utils/parsePrioritySearchPhrases";
import { MAX_LANDSCAPES_PER_REQUEST, MAX_SEARCH_PHRASE_LENGTH } from "@/utils/keywordValidation";

interface AddLandscapeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** If provided, the ASIN is pre-selected and the selector is hidden */
  preselectedAsin?: string;
  /** Called after a landscape is successfully added */
  onAdded?: () => void;
  /** The hero product the landscapes belong to; the submit is disabled without it. */
  heroProductId?: number;
  /** Called with the phrases the backend refused as duplicates, even if the dialog has since closed. */
  onDuplicates?: (phrases: string[]) => void;
}

export function AddLandscapeModal({
  open,
  onOpenChange,
  preselectedAsin,
  onAdded,
  heroProductId,
  onDuplicates,
}: AddLandscapeModalProps) {
  const { selectedBrandId } = useAppData();
  const [addAsinOpen, setAddAsinOpen] = useState(false);
  const [selectedAsin, setSelectedAsin] = useState(preselectedAsin || "");
  const [phrase, setPhrase] = useState("");

  const { mutate: addLandscapes, isPending: isAddLandscapesPending } = landscapeApi.useAddLandscapes();
  // A session starts when the dialog opens and ends when it closes. A result that
  // arrives after its session ended must not touch the field or the open state.
  const sessionRef = useRef(0);
  // `isPending` reaches the render on a batched schedule, so a second click in the same tick still
  // sees an enabled button; this ref closes that window.
  const submittingRef = useRef(false);
  useEffect(() => {
    sessionRef.current += 1;
  }, [open]);
  const { mutateAsync: addHeroProducts } = productApi.useAddHeroProducts();

  const resolvedAsin = preselectedAsin || selectedAsin;

  const phrases = parsePrioritySearchPhrases(phrase);
  const tooLongCount = phrases.filter((p) => Array.from(p).length > MAX_SEARCH_PHRASE_LENGTH).length;
  const excessCount = Math.max(0, phrases.length - MAX_LANDSCAPES_PER_REQUEST);
  const canSubmit =
    Boolean(resolvedAsin) && heroProductId !== undefined && phrases.length >= 1 && tooLongCount === 0 && excessCount === 0 && !isAddLandscapesPending;
  const submitLabel = phrases.length >= 2 ? `Add ${phrases.length} Landscapes` : "Add Landscape";

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      sessionRef.current += 1;
      setPhrase("");
      if (!preselectedAsin) setSelectedAsin("");
    }
    onOpenChange(nextOpen);
  };

  const handleSubmit = () => {
    if (!canSubmit || !selectedBrandId || heroProductId === undefined || submittingRef.current) return;

    submittingRef.current = true;
    const session = sessionRef.current;
    addLandscapes(
      { heroAsin: resolvedAsin, brandId: selectedBrandId, heroProductId, phrases },
      {
        onSuccess: ({ created, notCreated, duplicates }) => {
          // Duplicates reach the mount even when the session has ended.
          if (duplicates.length > 0) onDuplicates?.(duplicates);
          if (session !== sessionRef.current) return;
          if (notCreated.length === 0) {
            handleOpenChange(false);
            onAdded?.();
          } else if (created.length > 0 || duplicates.length > 0) {
            setPhrase(notCreated.join("\n"));
          }
          // Nothing created or refused: the field keeps exactly what the user typed.
        },
        onSettled: () => {
          submittingRef.current = false;
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>Add Landscapes</DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Add one or more priority search phrases, separated by commas or new lines. We'll create a keyword landscape for each.
          </p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* ASIN Selector — only shown when no preselected ASIN */}
          {/* {showAsinSelector && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Hero ASIN</Label>
              {brandAsins.length > 0 ? (
                <div className="flex gap-2">
                  <Select value={selectedAsin} onValueChange={setSelectedAsin}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select a Hero ASIN" />
                    </SelectTrigger>
                    <SelectContent>
                      {brandAsins.map((a) => (
                        <SelectItem key={a.id} value={a.asin}>
                          <div className="flex flex-col">
                            <span className="text-sm">{a.name}</span>
                            <span className="text-xs text-muted-foreground font-mono">
                              {a.asin}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline-on-white"
                    size="icon"
                    type="button"
                    onClick={() => setAddAsinOpen(true)}
                    title="Add Hero ASIN"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <p className="text-sm text-muted-foreground">
                    No Hero ASINs for this brand.
                  </p>
                  <Button
                    variant="outline-on-white"
                    size="sm"
                    onClick={() => setAddAsinOpen(true)}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add Hero ASIN
                  </Button>
                </div>
              )}
            </div>
          )} */}

          {/* Priority Search Phrase */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Priority Search Phrases</Label>
            <Textarea
              rows={4}
              placeholder={"e.g. mineral sunscreen face, tinted sunscreen\nor one phrase per line"}
              value={phrase}
              readOnly={isAddLandscapesPending}
              onChange={(e) => setPhrase(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Separate phrases with commas or new lines — each one creates its own landscape.
            </p>
            {tooLongCount >= 1 && (
              <p className="text-xs text-destructive">
                {tooLongCount === 1
                  ? `Max ${MAX_SEARCH_PHRASE_LENGTH} characters per phrase \u2014 1 phrase is too long.`
                  : `Max ${MAX_SEARCH_PHRASE_LENGTH} characters per phrase \u2014 ${tooLongCount} phrases are too long.`}
              </p>
            )}
            {excessCount >= 1 && (
              <p className="text-xs text-destructive">
                {`No more than ${MAX_LANDSCAPES_PER_REQUEST} phrases at a time \u2014 remove ${excessCount}.`}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline-on-white" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isAddLandscapesPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>

      <AddHeroAsinModal
        open={addAsinOpen}
        onOpenChange={setAddAsinOpen}
        onAddAsins={async (asins) => {
          if (!selectedBrandId) return;
          try {
            await addHeroProducts({ brandId: selectedBrandId, asins });
          } catch (error) {
            handleApiError(error);
          }
        }}
      />
    </Dialog>
  );
}
